(() => {
  'use strict';

  const root = document.querySelector('[data-initial-picture-capture]');
  if (!root) return;

  const nodes = {
    cameraPanel: root.querySelector('[data-camera-panel]'),
    cameraPreview: root.querySelector('[data-camera-preview]'),
    cameraStart: root.querySelector('[data-camera-start]'),
    cameraStop: root.querySelector('[data-camera-stop]'),
    cameraCapture: root.querySelector('[data-camera-capture]'),
    cameraHint: root.querySelector('[data-camera-hint]'),
    cameraLens: root.querySelector('[data-camera-lens]'),
    cameraLensWrap: root.querySelector('[data-camera-lens-wrap]'),
    itemName: root.querySelector('[data-capture-item-name]'),
    ticket: root.querySelector('[data-capture-ticket]'),
    error: root.querySelector('[data-capture-error]'),
    queueCount: root.querySelector('[data-capture-count]'),
    queueEmpty: root.querySelector('[data-capture-empty]'),
    queueItems: root.querySelector('[data-capture-items]'),
    completionCopy: root.querySelector('[data-completion-copy]'),
    complete: root.querySelector('[data-capture-complete]')
  };

  const state = {
    audioContext: null,
    authorized: false,
    cameraDevices: [],
    cameraSwitching: false,
    captureQueue: [],
    capturing: 0,
    completed: false,
    completing: false,
    photos: [],
    prepareQueue: [],
    preparing: 0,
    selectedCameraDeviceId: null,
    stream: null,
    token: null,
    uploadQueue: [],
    uploading: 0
  };

  // One prepare job and one upload job avoid overheating the phone and
  // competing uploads on weak yard connections. Capture remains available.
  const CAPTURE_ENCODE_CONCURRENCY = 1;
  const MAX_CAPTURE_BUFFER = 8;
  const PREPARE_CONCURRENCY = 1;
  const UPLOAD_CONCURRENCY = 1;
  const COMPLETION_ACK_GRACE_MS = 3500;
  const N8N_WEBHOOK_ORIGIN = 'https://n8n-tja1.srv1744220.hstgr.cloud';
  const ALLOWED_N8N_WEBHOOK_PATHS = new Set([
    '/webhook/inventory-initial-pictures-session',
    '/webhook/inventory-initial-pictures-stage',
    '/webhook/inventory-initial-pictures-finalize',
    '/webhook/inventory-initial-pictures-remove',
    '/webhook/inventory-initial-pictures-complete'
  ]);

  const config = readConfig(root);

  bindEvents();
  void initialise();

  function readConfig(element) {
    return {
      completeUrl: normaliseN8nWebhookEndpoint(element.dataset.completeUrl),
      finalizeUrl: normaliseN8nWebhookEndpoint(element.dataset.finalizeUrl),
      maxFileBytes: Math.max(400, Number(element.dataset.maxFileKilobytes) || 900) * 1024,
      maxLongEdge: Math.max(640, Number(element.dataset.maxLongEdge) || 1280),
      removeUrl: normaliseN8nWebhookEndpoint(element.dataset.removeUrl),
      sessionUrl: normaliseN8nWebhookEndpoint(element.dataset.sessionUrl),
      stageUrl: normaliseN8nWebhookEndpoint(element.dataset.stageUrl),
      uploadTimeoutMs: Math.max(30, Number(element.dataset.uploadTimeoutSeconds) || 90) * 1000,
    };
  }

  function normaliseN8nWebhookEndpoint(value) {
    if (!value) return null;

    try {
      const url = new URL(value);
      if (url.origin !== N8N_WEBHOOK_ORIGIN || !ALLOWED_N8N_WEBHOOK_PATHS.has(url.pathname)) {
        return null;
      }
      return url.toString();
    } catch (_error) {
      return null;
    }
  }

  function bindEvents() {
    nodes.cameraStart.addEventListener('click', () => {
      void startCamera();
    });
    nodes.cameraStop.addEventListener('click', stopCamera);
    nodes.cameraCapture.addEventListener('click', () => {
      void captureFrame();
    });
    if (nodes.cameraLens) {
      nodes.cameraLens.addEventListener('change', () => {
        void switchCamera(nodes.cameraLens.value);
      });
    }
    nodes.complete.addEventListener('click', () => {
      void completeCapture();
    });
    window.addEventListener('beforeunload', stopCamera);
    window.addEventListener('online', () => {
      if (!state.completed) {
        nodes.completionCopy.textContent = 'Connection restored. Retry any red photo before pressing Done.';
      }
    });
  }

  async function initialise() {
    if (!config.sessionUrl || !config.stageUrl || !config.finalizeUrl || !config.completeUrl || !config.removeUrl) {
      showFatalError('This test page is missing one of its secure server endpoints.');
      return;
    }

    const pageUrl = new URL(window.location.href);
    const token = pageUrl.searchParams.get('capture_token');
    if (!token || token.length > 2048) {
      showFatalError('This photo-capture link is missing or invalid. Return to the inventory board and open its Photos Link again.');
      return;
    }

    state.token = token;

    try {
      const session = await postJson(config.sessionUrl, { capture_token: state.token }, 30000);
      if (!session || typeof session.item_name !== 'string' || !session.item_name.trim()) {
        throw new Error('The capture session did not identify a vehicle.');
      }

      nodes.itemName.textContent = session.item_name.trim();
      nodes.ticket.textContent = session.ticket ? 'Ticket: ' + String(session.ticket) : '';
      state.authorized = true;
      nodes.cameraStart.disabled = false;
      nodes.cameraHint.textContent = 'Ready for rear camera';
      render();
    } catch (_error) {
      showFatalError('This photo-capture link could not be opened. Check your connection, then reopen the Photos Link from the inventory board.');
    }
  }

  async function startCamera() {
    if (!state.authorized || state.completed || state.cameraSwitching) return;
    clearError();
    void primeCaptureAudio();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showError('This browser cannot open the camera. Use the current version of Chrome on Android or Safari on iPhone.');
      return;
    }

    showCameraPanel();
    nodes.cameraStart.disabled = true;
    nodes.cameraCapture.disabled = true;

    if (state.stream) {
      nodes.cameraCapture.disabled = false;
      return;
    }

    nodes.cameraHint.textContent = 'Starting rear camera…';

    try {
      await attachCameraStream(state.selectedCameraDeviceId);
      await refreshCameraLensChoices();
      nodes.cameraCapture.disabled = false;
      nodes.cameraHint.textContent = 'Tap the red shutter for each photo';
    } catch (_error) {
      stopCamera();
      showError('Camera access was not available. Allow camera permission, then try again.');
    } finally {
      if (!state.stream && !state.completed) nodes.cameraStart.disabled = false;
    }
  }

  function showCameraPanel() {
    nodes.cameraPanel.hidden = false;
    document.documentElement.classList.add('initial-picture-camera-open');
    if (document.body) document.body.classList.add('initial-picture-camera-open');
  }

  function hideCameraPanel() {
    nodes.cameraPanel.hidden = true;
    document.documentElement.classList.remove('initial-picture-camera-open');
    if (document.body) document.body.classList.remove('initial-picture-camera-open');
  }

  function releaseCameraStream() {
    if (state.stream) {
      state.stream.getTracks().forEach((track) => track.stop());
      state.stream = null;
    }
    nodes.cameraPreview.pause();
    nodes.cameraPreview.srcObject = null;
  }

  function cameraConstraints(deviceId) {
    const video = {
      height: { ideal: 1080 },
      width: { ideal: 1920 }
    };
    if (deviceId) {
      video.deviceId = { exact: deviceId };
    } else {
      video.facingMode = { ideal: 'environment' };
    }
    return { audio: false, video };
  }

  async function attachCameraStream(deviceId) {
    const stream = await navigator.mediaDevices.getUserMedia(cameraConstraints(deviceId));
    try {
      nodes.cameraPreview.srcObject = stream;
      await nodes.cameraPreview.play();
      state.stream = stream;
      const track = stream.getVideoTracks()[0];
      const settings = track && typeof track.getSettings === 'function' ? track.getSettings() : {};
      state.selectedCameraDeviceId = deviceId || settings.deviceId || null;
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      nodes.cameraPreview.pause();
      nodes.cameraPreview.srcObject = null;
      throw error;
    }
  }

  async function refreshCameraLensChoices() {
    if (!nodes.cameraLens || !nodes.cameraLensWrap || !navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const candidates = rearCameraCandidates(devices);
      state.cameraDevices = candidates;
      nodes.cameraLens.textContent = '';

      if (candidates.length < 2) {
        nodes.cameraLensWrap.hidden = true;
        nodes.cameraLens.disabled = true;
        return;
      }

      candidates.forEach((device, index) => {
        const option = document.createElement('option');
        option.value = device.deviceId;
        option.textContent = cameraDeviceLabel(device, index);
        nodes.cameraLens.appendChild(option);
      });

      const activeDeviceId = candidates.some((device) => device.deviceId === state.selectedCameraDeviceId)
        ? state.selectedCameraDeviceId
        : candidates[0].deviceId;
      nodes.cameraLens.value = activeDeviceId;
      nodes.cameraLensWrap.hidden = false;
      nodes.cameraLens.disabled = false;
    } catch (_error) {
      // A camera still works if the browser declines to enumerate its lenses.
      nodes.cameraLensWrap.hidden = true;
      nodes.cameraLens.disabled = true;
    }
  }

  function rearCameraCandidates(devices) {
    const videoInputs = devices.filter((device) => device && device.kind === 'videoinput');
    const labeledRearInputs = videoInputs.filter((device) => !/(front|user)/i.test(String(device.label || '')));
    return labeledRearInputs.length >= 2 ? labeledRearInputs : [];
  }

  function cameraDeviceLabel(device, index) {
    const label = String(device.label || '').trim();
    if (/(ultra|wide|0[.,]5|0[.,]6)/i.test(label)) return 'Wide angle';
    if (/(tele|zoom)/i.test(label)) return 'Telephoto';
    if (label && !/^camera\s*\d*$/i.test(label)) return label;
    return 'Rear camera ' + String(index + 1);
  }

  async function switchCamera(deviceId) {
    if (
      !state.authorized ||
      state.completed ||
      state.cameraSwitching ||
      !deviceId ||
      deviceId === state.selectedCameraDeviceId
    ) return;

    clearError();
    state.cameraSwitching = true;
    const previousDeviceId = state.selectedCameraDeviceId;
    nodes.cameraCapture.disabled = true;
    if (nodes.cameraLens) nodes.cameraLens.disabled = true;
    nodes.cameraHint.textContent = 'Switching camera…';
    releaseCameraStream();

    try {
      await attachCameraStream(deviceId);
      await refreshCameraLensChoices();
      nodes.cameraHint.textContent = 'Tap the red shutter for each photo';
    } catch (_error) {
      try {
        await attachCameraStream(previousDeviceId);
        await refreshCameraLensChoices();
        nodes.cameraHint.textContent = 'The selected camera was unavailable. The previous rear camera is active.';
      } catch (_restoreError) {
        stopCamera();
      }
      showError('This phone did not allow that camera lens. Try another option or use the standard rear camera.');
    } finally {
      state.cameraSwitching = false;
      if (state.stream && !state.completed) nodes.cameraCapture.disabled = false;
      if (nodes.cameraLens && !nodes.cameraLensWrap.hidden) nodes.cameraLens.disabled = false;
    }
  }

  function stopCamera() {
    releaseCameraStream();
    hideCameraPanel();
    if (nodes.cameraLensWrap) nodes.cameraLensWrap.hidden = true;
    if (nodes.cameraLens) nodes.cameraLens.disabled = true;
    nodes.cameraCapture.disabled = true;
    if (!state.completed) nodes.cameraStart.disabled = !state.authorized;
  }

  function captureFrame() {
    if (!state.stream || state.completed) return;

    const pendingCaptures = state.captureQueue.length + state.capturing;
    if (pendingCaptures >= MAX_CAPTURE_BUFFER) {
      showError('The camera is catching up. Wait until a photo leaves Capturing before taking another.');
      return;
    }

    const width = nodes.cameraPreview.videoWidth;
    const height = nodes.cameraPreview.videoHeight;
    if (!width || !height) {
      showError('The camera is still starting. Wait one moment and try the shutter again.');
      return;
    }

    clearError();

    let canvas;
    try {
      canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: false, desynchronized: true });
      if (!context) throw new Error('Camera image processing is unavailable.');

      // Freeze this exact video frame synchronously before compression begins.
      // This keeps normal rapid taps from being silently ignored.
      context.drawImage(nodes.cameraPreview, 0, 0, width, height);
      const photo = createCapturedPhoto();
      state.photos.push(photo);
      state.captureQueue.push({ canvas, photo });
      triggerCaptureFeedback();
      render();
      pumpCaptureEncoding();
    } catch (_error) {
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
      }
      showError('The photo could not be captured. Keep the camera open and try again.');
    }
  }

  function pumpCaptureEncoding() {
    while (state.capturing < CAPTURE_ENCODE_CONCURRENCY && state.captureQueue.length) {
      const entry = state.captureQueue.shift();
      if (!entry || !entry.photo || entry.photo.removed) {
        if (entry && entry.canvas) {
          entry.canvas.width = 1;
          entry.canvas.height = 1;
        }
        continue;
      }

      state.capturing += 1;
      void encodeCapturedFrame(entry).finally(() => {
        state.capturing -= 1;
        pumpCaptureEncoding();
      });
    }
  }

  async function encodeCapturedFrame(entry) {
    const { canvas, photo } = entry;
    try {
      const sourceBlob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
      if (photo.removed) return;

      photo.sourceBlob = sourceBlob;
      photo.previewUrl = URL.createObjectURL(sourceBlob);
      photo.status = 'preparing';
      state.prepareQueue.push(photo);
      render();
      pumpPreparation();
    } catch (_error) {
      if (photo.removed) return;
      photo.status = 'failed';
      photo.error = 'Could not capture this photo';
      render();
    } finally {
      canvas.width = 1;
      canvas.height = 1;
    }
  }

  function triggerCaptureFeedback() {
    root.classList.remove('is-capture-flashing');
    // Force a new animation cycle for rapid, consecutive camera taps.
    void root.offsetWidth;
    root.classList.add('is-capture-flashing');
    window.setTimeout(() => root.classList.remove('is-capture-flashing'), 220);
    playCaptureSound();
  }

  async function primeCaptureAudio() {
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return;
    try {
      const context = state.audioContext || new AudioContextConstructor();
      state.audioContext = context;
      if (context.state === 'suspended') await context.resume();
    } catch (_error) {
      // The visual flash is the required feedback; audio is an enhancement.
    }
  }

  function playCaptureSound() {
    const context = state.audioContext;
    if (!context || context.state !== 'running') return;
    try {
      const now = context.currentTime;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(1300, now);
      oscillator.frequency.exponentialRampToValueAtTime(520, now + 0.07);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.09, now + 0.006);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.085);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(now);
      oscillator.stop(now + 0.09);
    } catch (_error) {
      // Do not affect picture capture if a browser declines synthetic audio.
    }
  }

  function createCapturedPhoto() {
    const sequence = state.photos.length + 1;
    return {
      abortUpload: null,
      directUploadComplete: false,
      error: '',
      fileReceipt: null,
      filename: 'vehicle-photo-' + String(sequence).padStart(3, '0') + '.jpg',
      height: null,
      id: createClientId(),
      previewUrl: '',
      progress: 0,
      removed: false,
      sequence,
      sourceBlob: null,
      shopifyFileId: null,
      stagedUpload: null,
      status: 'capturing',
      uploadBlob: null,
      width: null
    };
  }

  function createClientId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    const random = Math.random().toString(36).slice(2);
    return 'photo-' + Date.now().toString(36) + '-' + random;
  }

  function pumpPreparation() {
    while (state.preparing < PREPARE_CONCURRENCY && state.prepareQueue.length) {
      const photo = state.prepareQueue.shift();
      if (!photo || photo.removed || photo.status !== 'preparing') continue;

      state.preparing += 1;
      void preparePhoto(photo).finally(() => {
        state.preparing -= 1;
        pumpPreparation();
      });
    }
  }

  async function preparePhoto(photo) {
    try {
      const result = await compressForUpload(photo.sourceBlob, config.maxLongEdge, config.maxFileBytes);
      if (photo.removed) return;

      photo.uploadBlob = result.blob;
      photo.width = result.width;
      photo.height = result.height;
      photo.sourceBlob = null;
      replacePreview(photo, result.blob);
      photo.progress = 0;
      photo.status = 'ready';
      state.uploadQueue.push(photo);
      render();
      pumpUploads();
    } catch (_error) {
      if (photo.removed) return;
      photo.status = 'failed';
      photo.error = 'Could not prepare this photo';
      render();
    }
  }

  function replacePreview(photo, blob) {
    if (photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
    photo.previewUrl = URL.createObjectURL(blob);
  }

  function pumpUploads() {
    while (state.uploading < UPLOAD_CONCURRENCY && state.uploadQueue.length) {
      const photo = state.uploadQueue.shift();
      if (!photo || photo.removed || photo.status !== 'ready') continue;

      state.uploading += 1;
      void uploadPhoto(photo).finally(() => {
        state.uploading -= 1;
        pumpUploads();
      });
    }
  }

  async function uploadPhoto(photo) {
    photo.status = 'uploading';
    photo.phase = 'staging';
    photo.progress = 0;
    photo.error = '';
    render();

    try {
      let stagedUpload = photo.stagedUpload;
      if (!stagedUpload) {
        const stageResponse = await postJson(config.stageUrl, {
          capture_token: state.token,
          client_photo_id: photo.id,
          filename: photo.filename,
          height: photo.height,
          size_bytes: photo.uploadBlob.size,
          width: photo.width
        }, 30000);
        stagedUpload = validateStagedUpload(stageResponse);
        photo.stagedUpload = stagedUpload;
      }
      if (photo.removed) return;

      if (!photo.directUploadComplete) {
        photo.phase = 'uploading';
        render();
        await uploadBlobToStagedTarget(photo, stagedUpload);
        if (photo.removed) return;
        photo.directUploadComplete = true;
      }

      photo.phase = 'finalizing';
      photo.progress = 100;
      render();
      const finalizeResponse = await postJson(config.finalizeUrl, {
        capture_token: state.token,
        client_photo_id: photo.id,
        filename: photo.filename,
        height: photo.height,
        resource_url: stagedUpload.resourceUrl,
        stage_receipt: stagedUpload.stageReceipt,
        width: photo.width
      }, 30000);
      if (!finalizeResponse || finalizeResponse.accepted !== true || !finalizeResponse.shopify_file_id || !finalizeResponse.file_receipt) {
        throw new Error('Shopify file registration was not confirmed');
      }

      const shopifyFileId = String(finalizeResponse.shopify_file_id);
      const fileReceipt = String(finalizeResponse.file_receipt);
      if (photo.removed) {
        try {
          await postJson(config.removeUrl, {
            capture_token: state.token,
            client_photo_id: photo.id,
            file_receipt: fileReceipt,
            shopify_file_id: shopifyFileId
          }, 30000);
        } catch (_error) {
          // The completion request excludes removed client IDs, so it cannot
          // attach this file to monday even if delayed storage cleanup fails.
        }
        return;
      }

      photo.shopifyFileId = shopifyFileId;
      photo.fileReceipt = fileReceipt;
      photo.status = 'uploaded';
      photo.phase = '';
      photo.progress = 100;
      render();
    } catch (error) {
      if (photo.removed) return;
      if (photo.phase === 'uploading') {
        photo.stagedUpload = null;
        photo.directUploadComplete = false;
      }
      photo.status = 'failed';
      photo.phase = '';
      photo.error = uploadErrorMessage(error);
      photo.progress = 0;
      render();
    }
  }

  function validateStagedUpload(response) {
    const upload = response && response.accepted === true ? response.upload : null;
    if (!upload || typeof upload.url !== 'string' || typeof upload.resource_url !== 'string' || typeof upload.stage_receipt !== 'string') {
      throw new Error('The staged-upload target was not confirmed');
    }

    let targetUrl;
    try {
      targetUrl = new URL(upload.url);
    } catch (_error) {
      throw new Error('The staged-upload target was invalid');
    }
    if (targetUrl.protocol !== 'https:') {
      throw new Error('The staged-upload target was not secure');
    }
    let resourceUrl;
    try {
      resourceUrl = new URL(upload.resource_url);
    } catch (_error) {
      throw new Error('The staged-upload resource was invalid');
    }
    if (resourceUrl.protocol !== 'https:') {
      throw new Error('The staged-upload resource was not secure');
    }

    const method = String(upload.http_method || 'POST').toUpperCase();
    if (method !== 'POST' && method !== 'PUT') {
      throw new Error('The staged-upload method was not supported');
    }
    if (!/^[A-Za-z0-9_-]{16,}\.[a-f0-9]{64}$/i.test(upload.stage_receipt)) {
      throw new Error('The staged-upload receipt was invalid');
    }

    return {
      httpMethod: method,
      parameters: normaliseStagedParameters(upload.parameters),
      resourceUrl: resourceUrl.toString(),
      stageReceipt: upload.stage_receipt,
      url: targetUrl.toString()
    };
  }

  function normaliseStagedParameters(parameters) {
    if (Array.isArray(parameters)) {
      return parameters
        .filter((parameter) => parameter && typeof parameter.name === 'string')
        .map((parameter) => [parameter.name, String(parameter.value ?? '')]);
    }
    if (parameters && typeof parameters === 'object') {
      return Object.entries(parameters).map(([name, value]) => [name, String(value ?? '')]);
    }
    return [];
  }

  function uploadBlobToStagedTarget(photo, stagedUpload) {
    return new Promise((resolve, reject) => {
      const request = buildStagedUploadRequest(stagedUpload, photo);
      const xhr = new XMLHttpRequest();
      photo.abortUpload = () => xhr.abort();
      xhr.open(request.method, request.url, true);
      xhr.timeout = config.uploadTimeoutMs;
      xhr.withCredentials = false;
      xhr.responseType = 'text';
      Object.entries(request.headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || photo.removed) return;
        photo.progress = Math.max(1, Math.min(99, Math.round((event.loaded / event.total) * 100)));
        render();
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Upload timed out'));
      xhr.onabort = () => reject(new Error('Upload cancelled'));
      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new Error('Shopify staged upload was rejected'));
          return;
        }
        resolve();
      };

      xhr.send(request.body);
    }).finally(() => {
      photo.abortUpload = null;
    });
  }

  function buildStagedUploadRequest(stagedUpload, photo) {
    if (stagedUpload.httpMethod === 'POST') {
      const formData = new FormData();
      stagedUpload.parameters.forEach(([name, value]) => formData.append(name, value));
      formData.append('file', photo.uploadBlob, photo.filename);
      return {
        body: formData,
        headers: {},
        method: 'POST',
        url: stagedUpload.url
      };
    }

    const url = new URL(stagedUpload.url);
    const headers = {};
    stagedUpload.parameters.forEach(([name, value]) => {
      if (name.toLowerCase() === 'content-type') {
        headers[name] = value;
      } else {
        url.searchParams.append(name, value);
      }
    });
    if (!Object.keys(headers).some((name) => name.toLowerCase() === 'content-type')) {
      headers['Content-Type'] = photo.uploadBlob.type || 'image/jpeg';
    }
    return {
      body: photo.uploadBlob,
      headers,
      method: 'PUT',
      url: url.toString()
    };
  }

  function uploadErrorMessage(error) {
    if (!navigator.onLine) return 'No connection — retry when online';
    if (error && error.message === 'Upload timed out') return 'Upload timed out — retry';
    return 'Upload failed — retry';
  }

  async function retryPhoto(photo) {
    if (photo.removed || state.completed) return;
    clearError();
    photo.error = '';
    photo.progress = 0;

    if (photo.uploadBlob) {
      photo.status = 'ready';
      state.uploadQueue.push(photo);
      render();
      pumpUploads();
      return;
    }

    if (photo.sourceBlob) {
      photo.status = 'preparing';
      state.prepareQueue.push(photo);
      render();
      pumpPreparation();
    }
  }

  async function removePhoto(photo) {
    if (photo.removed || state.completed) return;
    clearError();

    if (photo.status === 'uploaded') {
      photo.status = 'removing';
      render();
      try {
        const response = await postJson(config.removeUrl, {
          capture_token: state.token,
          client_photo_id: photo.id,
          file_receipt: photo.fileReceipt,
          shopify_file_id: photo.shopifyFileId
        }, 30000);
        if (!response || response.accepted !== true) {
          throw new Error('The stored photo removal was not confirmed');
        }
      } catch (_error) {
        photo.status = 'uploaded';
        photo.error = 'Could not remove the uploaded photo';
        showError('The uploaded photo could not be removed. Try again before pressing Done.');
        render();
        return;
      }
    }

    if (photo.abortUpload) photo.abortUpload();
    photo.removed = true;
    photo.status = 'removed';
    photo.sourceBlob = null;
    photo.uploadBlob = null;
    if (photo.previewUrl) {
      URL.revokeObjectURL(photo.previewUrl);
      photo.previewUrl = '';
    }
    render();
  }

  async function completeCapture() {
    if (!canComplete()) return;
    clearError();
    state.completing = true;
    render();

    try {
      const files = state.photos
        .filter((photo) => !photo.removed && photo.status === 'uploaded')
        .map((photo) => ({
          client_photo_id: photo.id,
          file_receipt: photo.fileReceipt,
          filename: photo.filename,
          shopify_file_id: photo.shopifyFileId
        }));
      const completionRequest = postJson(config.completeUrl, {
        capture_token: state.token,
        files
      }, 30000, { keepalive: true });
      const outcome = await Promise.race([
        completionRequest.then(
          (response) => ({ kind: 'response', response }),
          (error) => ({ error, kind: 'error' })
        ),
        delay(COMPLETION_ACK_GRACE_MS).then(() => ({ kind: 'grace' }))
      ]);

      if (outcome.kind === 'error') {
        throw outcome.error;
      }
      if (outcome.kind === 'response') {
        if (!outcome.response || outcome.response.accepted !== true) {
          throw new Error('The server did not accept the completion request.');
        }
        state.token = null;
        state.completing = false;
        finishCapture(files.length);
        confirmTransferAccepted(files.length);
        return;
      }

      // Hostinger n8n keeps this particular webhook response open while the
      // sequential Monday worker runs. The browser has already dispatched a
      // keepalive request and each green photo is safely stored in Shopify.
      state.completing = false;
      finishCapture(files.length);
      void completionRequest.then((response) => {
        if (!response || response.accepted !== true) {
          reopenCompletionAfterLateFailure();
          return;
        }
        state.token = null;
        confirmTransferAccepted(files.length);
      }).catch(() => {
        reopenCompletionAfterLateFailure();
      });
    } catch (_error) {
      showError('The photos are uploaded, but the final request did not reach the server. Keep this page open and press Done again.');
    } finally {
      if (!state.completed) {
        state.completing = false;
        render();
      }
    }
  }

  function finishCapture(fileCount) {
    state.completed = true;
    stopCamera();
    const count = Math.max(1, Number(fileCount) || 0);
    nodes.completionCopy.textContent = photoCountCopy(count) + ' ' + (count === 1 ? 'is' : 'are') + ' secure. The server is transferring ' + (count === 1 ? 'it' : 'them') + ' to the hidden VIN pictures column on Monday.com. You can move to the next vehicle.';
    render();
  }

  function confirmTransferAccepted(fileCount) {
    const count = Math.max(1, Number(fileCount) || 0);
    nodes.completionCopy.textContent = photoCountCopy(count) + ' ' + (count === 1 ? 'is' : 'are') + ' secure. The server accepted the transfer to the hidden VIN pictures column on Monday.com.';
    render();
  }

  function photoCountCopy(count) {
    return String(count) + (count === 1 ? ' green photo' : ' green photos');
  }

  function reopenCompletionAfterLateFailure() {
    if (!state.completed) return;
    state.completed = false;
    nodes.cameraStart.disabled = !state.authorized;
    nodes.completionCopy.textContent = 'The green photos are still secure, but the final server request needs a retry.';
    showError('The final transfer was not confirmed. Keep this page open and press Done again.');
    render();
  }

  function canComplete() {
    const visiblePhotos = state.photos.filter((photo) => !photo.removed);
    return state.authorized &&
      !state.completed &&
      !state.completing &&
      visiblePhotos.some((photo) => photo.status === 'uploaded') &&
      state.photos.every((photo) => photo.removed || photo.status === 'uploaded');
  }

  function render() {
    const visiblePhotos = state.photos.filter((photo) => !photo.removed);
    nodes.queueCount.textContent = visiblePhotos.length + (visiblePhotos.length === 1 ? ' photo' : ' photos');
    nodes.queueEmpty.hidden = visiblePhotos.length > 0;
    nodes.queueItems.textContent = '';

    visiblePhotos.forEach((photo) => {
      nodes.queueItems.appendChild(renderPhoto(photo));
    });

    nodes.complete.disabled = !canComplete();
    if (!state.completed && !state.completing && !canComplete()) {
      nodes.completionCopy.textContent = completionHint(visiblePhotos);
    }
    if (state.completing) {
      nodes.completionCopy.textContent = 'Starting the server-side monday transfer…';
    }
  }

  function renderPhoto(photo) {
    const item = document.createElement('li');
    item.className = 'initial-picture-capture__item';
    item.dataset.status = photo.status;

    if (photo.previewUrl) {
      const thumbnail = document.createElement('img');
      thumbnail.className = 'initial-picture-capture__thumbnail';
      thumbnail.alt = 'Photo ' + photo.sequence + ' preview';
      thumbnail.src = photo.previewUrl;
      item.appendChild(thumbnail);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'initial-picture-capture__thumbnail initial-picture-capture__thumbnail--placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = '…';
      item.appendChild(placeholder);
    }

    const copy = document.createElement('div');
    copy.className = 'initial-picture-capture__item-copy';
    const title = document.createElement('span');
    title.className = 'initial-picture-capture__item-title';
    title.textContent = 'Photo ' + photo.sequence;
    const status = document.createElement('span');
    status.className = 'initial-picture-capture__item-status';
    status.textContent = photoStatusLabel(photo);
    copy.append(title, status);

    if (photo.status === 'uploading') {
      const progress = document.createElement('progress');
      progress.className = 'initial-picture-capture__progress';
      progress.max = 100;
      progress.value = photo.progress;
      progress.setAttribute('aria-label', 'Photo ' + photo.sequence + ' upload progress');
      copy.appendChild(progress);
    }
    item.appendChild(copy);

    const actions = document.createElement('div');
    actions.className = 'initial-picture-capture__item-actions';
    if (photo.status === 'failed') {
      const retry = makeActionButton('Retry', 'initial-picture-capture__queue-action--retry', () => {
        void retryPhoto(photo);
      });
      actions.appendChild(retry);
    }

    if (photo.status !== 'removing') {
      const remove = makeActionButton('Remove', 'initial-picture-capture__queue-action--remove', () => {
        void removePhoto(photo);
      });
      remove.disabled = state.completing || state.completed;
      actions.appendChild(remove);
    }
    item.appendChild(actions);
    return item;
  }

  function makeActionButton(label, modifier, onClick) {
    const button = document.createElement('button');
    button.className = 'initial-picture-capture__queue-action ' + modifier;
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  function photoStatusLabel(photo) {
    if (photo.status === 'capturing') return 'Capturing…';
    if (photo.status === 'preparing') return 'Preparing and compressing…';
    if (photo.status === 'ready') return 'Waiting to upload…';
    if (photo.status === 'uploading' && photo.phase === 'staging') return 'Requesting secure upload…';
    if (photo.status === 'uploading' && photo.phase === 'finalizing') return 'Saving to Shopify…';
    if (photo.status === 'uploading') return 'Uploading ' + photo.progress + '%';
    if (photo.status === 'uploaded') return '✓ Uploaded';
    if (photo.status === 'removing') return 'Removing…';
    return photo.error || 'Retry needed';
  }

  function completionHint(visiblePhotos) {
    if (!visiblePhotos.length) return 'Take at least one photo before pressing Done.';
    if (visiblePhotos.some((photo) => photo.status === 'failed')) {
      return 'Retry or remove each red photo before pressing Done.';
    }
    if (visiblePhotos.some((photo) => photo.status !== 'uploaded')) {
      return 'Done becomes available after every photo is green or removed.';
    }
    return 'Done is ready.';
  }

  async function compressForUpload(sourceBlob, maxLongEdge, maxFileBytes) {
    const decoded = await decodeImage(sourceBlob);
    try {
      let dimensions = fitDimensions(decoded.width, decoded.height, maxLongEdge);
      const qualities = [0.88, 0.8, 0.72, 0.64, 0.56, 0.48, 0.4, 0.32];
      let lastBlob = null;

      for (let reduction = 0; reduction < 12; reduction += 1) {
        for (const quality of qualities) {
          const blob = await drawAndEncode(decoded.drawable, dimensions.width, dimensions.height, quality);
          lastBlob = blob;
          if (blob.size <= maxFileBytes) {
            return { blob, height: dimensions.height, width: dimensions.width };
          }
        }

        const longEdge = Math.max(dimensions.width, dimensions.height);
        if (longEdge <= 128) break;
        const nextLongEdge = Math.max(128, Math.floor(longEdge * 0.8));
        dimensions = fitDimensions(decoded.width, decoded.height, nextLongEdge);
      }

      if (lastBlob && lastBlob.size <= maxFileBytes) {
        return { blob: lastBlob, height: dimensions.height, width: dimensions.width };
      }
      throw new Error('Unable to reduce photo below the configured upload size.');
    } finally {
      decoded.cleanup();
    }
  }

  async function decodeImage(blob) {
    if (typeof window.createImageBitmap === 'function') {
      const bitmap = await window.createImageBitmap(blob);
      return {
        cleanup: () => bitmap.close(),
        drawable: bitmap,
        height: bitmap.height,
        width: bitmap.width
      };
    }

    return new Promise((resolve, reject) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(blob);
      image.onload = () => {
        resolve({
          cleanup: () => URL.revokeObjectURL(objectUrl),
          drawable: image,
          height: image.naturalHeight,
          width: image.naturalWidth
        });
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('The captured image could not be decoded.'));
      };
      image.src = objectUrl;
    });
  }

  function fitDimensions(sourceWidth, sourceHeight, maxLongEdge) {
    const sourceLongEdge = Math.max(sourceWidth, sourceHeight);
    const scale = Math.min(1, maxLongEdge / sourceLongEdge);
    return {
      height: Math.max(1, Math.round(sourceHeight * scale)),
      width: Math.max(1, Math.round(sourceWidth * scale))
    };
  }

  async function drawAndEncode(drawable, width, height, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Photo compression is unavailable.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(drawable, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    canvas.width = 1;
    canvas.height = 1;
    return blob;
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Image encoding failed.'));
        }
      }, type, quality);
    });
  }

  async function postJson(url, body, timeoutMs, options = {}) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, {
        body: JSON.stringify(body),
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        keepalive: options.keepalive === true,
        method: 'POST',
        mode: 'cors',
        signal: controller.signal
      });
      const responseBody = parseJson(await response.text());
      if (!response.ok) {
        throw new Error(responseBody && responseBody.error ? String(responseBody.error) : 'Request failed');
      }
      return responseBody;
    } finally {
      window.clearTimeout(timer);
    }
  }

  function delay(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  function parseJson(value) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function showFatalError(message) {
    showError(message);
    nodes.cameraStart.disabled = true;
    nodes.cameraCapture.disabled = true;
    nodes.complete.disabled = true;
    nodes.cameraHint.textContent = 'Camera unavailable';
  }

  function showError(message) {
    nodes.error.textContent = message;
    nodes.error.hidden = false;
  }

  function clearError() {
    nodes.error.textContent = '';
    nodes.error.hidden = true;
  }
})();


